import React, { useEffect, useState } from "react";
import ThemedChartLight from "./ThemedChartLight";
import ThemedChartDark from "./ThemedChartDark";

import { genericColors } from "../utils/chartUtils";
import styles from "../utils/themeStyles";
import { useVsiAppContext } from "../stores/VsiApp";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";

function DonutChart({
											data,
											title,
											height,
											enableLegend,
											seriesName,
											decimals,
											disableLegend,
											innerSize,
											disableLabels,
											colors,
											alignTitle,
											subtitle,
											tooltipFormat,
}) {
	const { theme, isTabletScreen } = useVsiAppContext();
	const basicTextStyle = {
		color: styles[theme].textColor,
		fontFamily: styles[theme].fontFamily,
	};

	const [chartOptions, setChartOptions] = useState({
			mapNavigation: {
					enabled: false,
				},
				exporting: {
					enabled: false,
				},
				credits: {
					enabled: false,
				},
				chart: {
					height: height || undefined,
					backgroundColor: 'transparent',
					color: styles[theme].textColor,
					type: 'pie',
				},
				tooltip: {
					backgroundColor: styles[theme].altBgColor,
					style: basicTextStyle,
					valueDecimals: decimals || 0,
					pointFormat: tooltipFormat,
				},
				title: {
					text: title || '',
					align: alignTitle || 'left',
					margin: 10,
					color: styles[theme].textColor,
					style: {
						...basicTextStyle,
						fontSize: '1rem',
					},
				},
				subtitle,
				plotOptions: {
					pie: {
						innerSize: innerSize ? innerSize : 150,
						allowPointSelect: true,
						floating: true,
						cursor: 'pointer',
						borderWidth: (data?.length && data?.length > 0 && ([...data] || []).every(datum => datum?.y > 0)) ? 1 : 0,
						dataLabels: {
							enabled: disableLabels ? !disableLabels : true,
							distance: -40,
							format: '{point.percentage:.1f} %',
							filter: {
								operator: '>',
								property: 'percentage',
								value: 8
							},
							color: styles[theme].textColor,
							style: {
								textOutline: "none"
							}
						},
						showInLegend: true,
					},
				},
				colors: colors || genericColors,

				series: {
					type: "pie",
					colorByPoint: true,
					data: data || [],
				},
				legend: {
					enabled: (enableLegend || ([...data] || []).every(datum => datum?.name?.length < 10)) && !disableLegend,
					itemStyle: {
						color: styles[theme].sideColor
					}
				},
			}
	);

	useEffect(() => {
		const basicTextStyle = {
			color: styles[theme].textColor,
			fontFamily: styles[theme].fontFamily,
		};
		setChartOptions({
			...chartOptions,
			chart: {
				...chartOptions.chart,
				height: height || undefined,
				color: styles[theme].textColor,
			},
			title: {
				...chartOptions.title,
				text: title || '',
				align: alignTitle || 'left',
				style: {
						...basicTextStyle,
						fontSize: '1rem',
				},
			},
			subtitle,
			tooltip: {
				...chartOptions.tooltip,
				backgroundColor: styles[theme].altBgColor,
				style: basicTextStyle,
				pointFormat: tooltipFormat
			},

			series: {
				...chartOptions.series,
				name: seriesName || 'Value',
				data: data || [],
			},
			plotOptions: {
				...chartOptions.plotOptions,
				pie: {
					...chartOptions.plotOptions.pie,
						borderWidth: (data?.length && data?.length > 0 && ([...data] || []).every(datum => datum?.y > 0)) ? 1 : 0,
					dataLabels: {
						enabled: disableLabels ? !disableLabels : true,
						distance: -40,
						format: '{point.percentage:.1f} %',
						filter: {
							operator: '>',
							property: 'percentage',
							value: 8
						},
						color: styles[theme].textColor,
						style: {
							textOutline: "none"
						}
					},
				},
			},
			colors: colors || genericColors,
			legend: {
				...chartOptions.legend,
				maxHeight: 125,
				enabled: (enableLegend || ([...data] || []).every(datum => datum?.name?.length <= 20)) && !disableLegend,
				itemStyle: {
					color: styles[theme].sideColor
				}
			}
		});
		// eslint-disable-next-line
	}, [data, theme, isTabletScreen, colors, enableLegend, disableLegend, disableLabels, seriesName, height, alignTitle, title, subtitle, tooltipFormat]);

	return "light" ? (
			<ThemedChartLight options={chartOptions}
			                  constructorType={'chart'}/>
		): (
			<ThemedChartDark options={chartOptions}
			                 constructorType={'chart'}/>
		)
}

DonutChart.displayName = "DonutChart"

export default withErrorBoundary(DonutChart, GeneralErrorBoundary);
